import { useState } from 'react';
import { Building2 } from 'lucide-react';
import { ROUTES } from '@/lib/constants/routes';
import { LinkStatusCard } from '@/components/common/LinkStatusCard';
import { toast } from '@/lib/toast';
import { getApiErrorCode } from '@/lib/utils/error.utils';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { useRequestStartupJoin } from '../hooks/useRequestStartupJoin';
import { CREATE_STARTUP_STRINGS } from '../constants';

interface StartupAlreadyExistsStateProps {
  /**
   * The Backend's STARTUP_DOMAIN_ALREADY_REGISTERED detail - the bare name
   * of the existing company (e.g. "Acme Inc"), highlighted inline. Falls
   * back to generic copy when absent (e.g. a network-layer failure with no
   * structured error body).
   */
  existingCompanyName?: string;
  /** An extra line under the description; the Issue Report journey says who can buy (FR-225). */
  note?: string;
  /** Full column width with no centring box, under the journey's progress bar (see `LinkStatusCard`). */
  inline?: boolean;
}

/**
 * "Startup Profile Already Exists" - shown by `CreateStartupPage` in place
 * of the 4-step wizard when `useStartupEligibility` reports
 * STARTUP_DOMAIN_ALREADY_REGISTERED (FR-13), so the user finds out before
 * filling anything out rather than at Step 4's work-email OTP send.
 *
 * "Request Access" is fire-and-forget (no pending-request record): it just
 * asks the Backend to notify every existing team member holding the invite
 * permission (email + in-app alert) that someone from their organization
 * wants to join - the admin still uses the existing "Invite a Team Member"
 * dialog to actually add them.
 */
export function StartupAlreadyExistsState({ existingCompanyName, note, inline }: StartupAlreadyExistsStateProps) {
  const S = CREATE_STARTUP_STRINGS;
  const requestJoin = useRequestStartupJoin();
  // Set on a real success AND on JOIN_REQUEST_COOLDOWN (the Backend's
  // anti-spam rate limit - functionally "already sent," not a failure) -
  // requestJoin.isSuccess alone would leave the button re-clickable after a
  // cooldown rejection, defeating the point of that limit.
  const [requestSent, setRequestSent] = useState(false);

  const description = existingCompanyName ? (
    <>
      {S.STARTUP_EXISTS_DESCRIPTION_PREFIX}
      <span className="font-semibold text-navy">{existingCompanyName}</span>
      {S.STARTUP_EXISTS_DESCRIPTION_SUFFIX}
    </>
  ) : (
    S.STARTUP_EXISTS_DESCRIPTION_FALLBACK
  );
  // A span, not a <p>: the card renders the body inside its own paragraph.
  const body = note ? (
    <>
      {description}
      <span className="mt-2 block">{note}</span>
    </>
  ) : (
    description
  );

  const handleRequestAccess = () => {
    requestJoin.mutate(undefined, {
      onSuccess: () => {
        setRequestSent(true);
        toast.success(S.STARTUP_EXISTS_REQUEST_SUCCESS);
      },
      onError: (error) => {
        if (getApiErrorCode(error) === API_ERROR_CODES.JOIN_REQUEST_COOLDOWN) {
          // Styled as success (not error) - the button flips to the same
          // "Request Sent" state a real success would, so a red toast here
          // would contradict its own reassuring copy.
          setRequestSent(true);
          toast.success(S.STARTUP_EXISTS_REQUEST_COOLDOWN);
          return;
        }
        toast.error(S.STARTUP_EXISTS_REQUEST_ERROR);
      },
    });
  };

  return (
    <LinkStatusCard
      icon={<Building2 size={32} />}
      heading={S.STARTUP_EXISTS_TITLE}
      body={body}
      ctaLabel={S.STARTUP_EXISTS_ACTION}
      ctaTo={ROUTES.DASHBOARD}
      ctaVariant="link"
      inline={inline}
      primaryAction={{
        label: requestSent ? S.STARTUP_EXISTS_REQUEST_SENT : S.STARTUP_EXISTS_REQUEST_ACTION,
        onClick: handleRequestAccess,
        disabled: requestJoin.isPending || requestSent,
        pendingLabel: requestJoin.isPending ? S.STARTUP_EXISTS_REQUEST_SENDING : undefined,
      }}
    />
  );
}
